import {
  log as logFeedback,
  type FeedbackResponse,
} from "@indeed/signals-schema/com/indeed/signals/schema/jspremium/Feedback";
import React, {
  createContext,
  type Dispatch,
  type SetStateAction,
  useContext,
  useState,
} from "react";

import { getTareData } from "../../../../../../shared/native-modules/tare/TareDataContext";
import { usePromise } from "../../../../../../shared/util/usePromise";
import { useViewJobDataContext } from "../../../../context/ViewJobDataContext";
import { FeedbackPopup, type FeedbackPopupProps } from "../components/FeedbackPopup";
import { InfoPopup, type InfoPopupProps } from "../components/InfoPopup";

interface FeedbackData {
  provider: string;
  accountId: number;
  proctorGroups: { array: string[] };
  hostId: string;
  feedbackText?: { string: string };
  feedbackResponse?: { FeedbackResponse: FeedbackResponse };
  jobKey?: { string: string };
  country?: { string: string };
  accountKey?: { string: string };
  mobtk?: { string: string };
}

type PopoverProps =
  | (FeedbackPopupProps & { modalType: "FEEDBACK" })
  | (InfoPopupProps & { modalType?: "DEFAULT" });

type ContextT = {
  setApplicantInsightsPopoverProps: Dispatch<SetStateAction<PopoverProps>>;
  dismissApplicantInsightsPopover: () => void;
};

const ModalContext = createContext<ContextT>({
  setApplicantInsightsPopoverProps: (q) => q,
  dismissApplicantInsightsPopover: () => undefined,
});

export function useModalContext() {
  return useContext(ModalContext);
}

const APPLICANT_INSIGHTS_PROVIDER_ID = "mosaic-provider-js-premium-applicant-insights";

export const ApplicantInsightsModalContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const viewJobDataContext = useViewJobDataContext();
  const [, appModel] = usePromise(getTareData);

  const [applicantInsightsPopoverProps, setApplicantInsightsPopoverProps] = useState<PopoverProps>({
    isOpen: false,
    onDismiss: () => undefined,
  });

  const logApplicantInsightsFeedback = (
    feedbackText?: string,
    feedbackResponse?: FeedbackResponse,
  ) => {
    const feedback: FeedbackData = {
      provider: APPLICANT_INSIGHTS_PROVIDER_ID,
      accountId: appModel?.accountStatus?.accountId ? Number(appModel.accountStatus.accountId) : 0,
      proctorGroups: { array: viewJobDataContext?.proctorGroups?.split(",") || [] },
      hostId: viewJobDataContext?.initialData?.mosaicData?.host?.hostId || "",
    };

    // Add optional fields only when data exists
    if (feedbackText) {
      feedback.feedbackText = { string: feedbackText };
    }
    if (feedbackResponse) {
      feedback.feedbackResponse = { FeedbackResponse: feedbackResponse };
    }
    if (viewJobDataContext?.initialData?.jobKey) {
      feedback.jobKey = { string: viewJobDataContext.initialData.jobKey };
    }
    if (viewJobDataContext?.initialData?.country) {
      feedback.country = { string: viewJobDataContext.initialData.country };
    }
    if (viewJobDataContext?.initialData?.accountKey) {
      feedback.accountKey = { string: viewJobDataContext.initialData.accountKey };
    }
    if (viewJobDataContext?.initialData?.mobtk) {
      feedback.mobtk = { string: viewJobDataContext.initialData.mobtk };
    }
    logFeedback(feedback);
  };

  const dismissApplicantInsightsPopover = () => {
    setApplicantInsightsPopoverProps((prev: PopoverProps) => {
      if (prev.modalType === "FEEDBACK") {
        logApplicantInsightsFeedback(prev.feedbackText, prev.feedbackResponse);
      }
      return { ...prev, isOpen: false };
    });
  };

  return (
    <ModalContext.Provider
      value={{
        setApplicantInsightsPopoverProps,
        dismissApplicantInsightsPopover,
      }}
    >
      {children}
      {applicantInsightsPopoverProps.isOpen && (
        <>
          {applicantInsightsPopoverProps.modalType === "FEEDBACK" ? (
            <FeedbackPopup {...applicantInsightsPopoverProps} />
          ) : (
            <InfoPopup {...applicantInsightsPopoverProps} />
          )}
        </>
      )}
    </ModalContext.Provider>
  );
};
